/**
 * Credit Purchase Calculator Component
 *
 * Displays an inline calculator for one-time credit purchases, showing dynamic
 * pricing based on image count with real-time calculations.
 *
 * @module components/CreditPurchaseCalculator
 */

import React, { useState, useEffect } from 'react';

// Facebook Pixel TypeScript declaration
declare global {
  interface Window {
    fbq?: (command: string, eventName: string, params?: Record<string, unknown>) => void;
  }
}

// Credit pricing constants (matches backend pricing)
const CREDIT_PRICE_USD = 0.001; // $0.001 per credit
const MIN_CREDITS = 1000; // Minimum 1000 credits
const MIN_PURCHASE_USD = 1.0; // $1.00 minimum

export interface CreditPurchaseCalculatorProps {
  /** Initial image count (from URL or sessionStorage) */
  initialImageCount?: number;
  /** Whether to auto-expand the calculator (true when from=limit) */
  autoExpanded?: boolean;
  /** Callback when user clicks purchase button */
  onPurchaseClick: (imageCount: number, credits: number, totalCost: number) => void;
}

/**
 * Calculates credits and cost for given image count
 */
function calculatePricing(imageCount: number) {
  // 1 credit per image
  const creditsForImages = imageCount;

  // Enforce minimum credits
  const credits = Math.max(creditsForImages, MIN_CREDITS);

  // Calculate cost (enforce minimum $1.00)
  const calculatedCost = credits * CREDIT_PRICE_USD;
  const totalCost = Math.max(calculatedCost, MIN_PURCHASE_USD);

  // Check if minimum is being applied
  const isMinimumApplied = credits === MIN_CREDITS && imageCount < MIN_CREDITS;

  return { credits, totalCost, isMinimumApplied };
}

export function CreditPurchaseCalculator({
  initialImageCount,
  autoExpanded = false,
  onPurchaseClick
}: CreditPurchaseCalculatorProps) {
  const [imageCount, setImageCount] = useState(initialImageCount || 100);
  const [isExpanded, setIsExpanded] = useState(autoExpanded);
  const isReadOnly = initialImageCount !== undefined;

  // Recalculate when image count changes
  const { credits, totalCost, isMinimumApplied } = calculatePricing(imageCount);

  // Auto-expand when coming from limit
  useEffect(() => {
    if (autoExpanded) {
      setIsExpanded(true);
    }
  }, [autoExpanded]);

  const handleImageCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      setImageCount(value);
    }
  };

  const handlePurchaseClick = () => {
    // Track Facebook Pixel AddToCart event (November 2025 Meta best practices)
    // Using fixed content_id with quantity for better optimization & remarketing
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'AddToCart', {
        value: totalCost,
        currency: 'USD',
        content_ids: ['sortingcat_credits'],
        content_type: 'product',
        contents: [{
          id: 'sortingcat_credits',
          quantity: credits,
          item_price: CREDIT_PRICE_USD
        }],
        num_items: 1,
      });
      console.log(`📊 Facebook Pixel: AddToCart tracked - ${credits} credits, $${totalCost.toFixed(2)}`);
    }

    onPurchaseClick(imageCount, credits, totalCost);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border-2 border-purple-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            One-Time Credit Purchase
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Pay once, process your images immediately
          </p>
        </div>
        {!autoExpanded && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-purple-600 hover:text-purple-700 font-medium text-sm"
          >
            {isExpanded ? 'Hide' : 'Show Calculator'}
          </button>
        )}
      </div>

      {/* Calculator (collapsible) */}
      {isExpanded && (
        <div className="space-y-4">
          {/* Image Count Input */}
          <div>
            <label htmlFor="image-count" className="block text-sm font-medium text-gray-700 mb-2">
              Number of Images
            </label>
            <input
              id="image-count"
              type="number"
              min="1"
              value={imageCount}
              onChange={handleImageCountChange}
              readOnly={isReadOnly}
              className={`w-full px-4 py-2 border rounded-md ${
                isReadOnly
                  ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                  : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent'
              }`}
              placeholder="Enter number of images"
            />
            {isReadOnly && (
              <p className="text-xs text-gray-600 mt-1">
                Based on your {imageCount.toLocaleString()} selected images
              </p>
            )}
          </div>

          {/* Pricing Breakdown */}
          <div className="bg-purple-50 rounded-md p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">Images:</span>
              <span className="text-sm font-medium text-gray-900">
                {imageCount.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">Credits Needed:</span>
              <span className="text-sm font-medium text-gray-900">
                {credits.toLocaleString()}
                {isMinimumApplied && (
                  <span className="text-xs text-purple-600 ml-2">(minimum)</span>
                )}
              </span>
            </div>

            <div className="border-t border-purple-200 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-base font-semibold text-gray-900">Total Cost:</span>
                <span className="text-2xl font-bold text-purple-600">
                  ${totalCost.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Minimum Notice */}
            {isMinimumApplied && (
              <p className="text-xs text-gray-600 mt-2">
                Minimum purchase: {MIN_CREDITS.toLocaleString()} credits (${MIN_PURCHASE_USD.toFixed(2)})
              </p>
            )}

            {/* Pricing Info */}
            <p className="text-xs text-gray-500 mt-2">
              1 credit = 1 image • ${(CREDIT_PRICE_USD * 1000).toFixed(2)} per 1,000 credits
            </p>
          </div>

          {/* Purchase Button */}
          <button
            onClick={handlePurchaseClick}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-md transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <span>Purchase {credits.toLocaleString()} Credits</span>
            <span className="text-lg">(${totalCost.toFixed(2)})</span>
          </button>

          {/* Payment Methods Info */}
          <p className="text-xs text-center text-gray-500">
            Secure checkout with Stripe • Card, Apple Pay, and Link accepted
          </p>
        </div>
      )}

      {/* Collapsed State - Just show the essential info */}
      {!isExpanded && (
        <div className="flex items-center justify-between bg-purple-50 rounded-md p-4">
          <div>
            <p className="text-sm text-gray-700">
              <span className="font-medium">{imageCount.toLocaleString()} images</span> = {credits.toLocaleString()} credits
            </p>
            <p className="text-xs text-gray-500 mt-1">
              ${(CREDIT_PRICE_USD * 1000).toFixed(2)} per 1,000 credits • ${MIN_PURCHASE_USD.toFixed(2)} minimum
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-purple-600">${totalCost.toFixed(2)}</p>
            <button
              onClick={handlePurchaseClick}
              className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Purchase Now →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
